import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

// Configure axios instance for file service
const fileServiceClient = axios.create({
  baseURL: config.api.fileServiceUrl,
  timeout: 600000, // 10 minutes for large file uploads and processing
  headers: {}
});

// Add request interceptor to include auth token
fileServiceClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// On 401, attempt a token refresh and retry once before logging out
applyAuthRetryInterceptor(fileServiceClient);

// Proactively refresh token before any request if within 30-minute expiry window
applyTokenRefreshInterceptor(fileServiceClient);

export const fileService = {
  // Upload a single file (with size check for presigned URL)
  uploadFile: async (file, fileType, timestamp = null) => {
    try {
      const MAX_DIRECT_UPLOAD_SIZE = 3 * 1024 * 1024; // 3MB threshold for direct upload
      
      // Check file size and use appropriate upload method
      if (file.size > MAX_DIRECT_UPLOAD_SIZE) {
        console.log(`File size (${(file.size / 1024 / 1024).toFixed(2)} MB) exceeds direct upload limit. Using presigned URL.`);
        return await fileService.uploadLargeFile(file, fileType, timestamp);
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', fileType);
      formData.append('convert_to_parquet', 'true');
      // Include timestamp if provided
      if (timestamp) {
        formData.append('timestamp', timestamp);
      }
      if (timestamp) {
        formData.append('timestamp', timestamp);
      }

      const response = await fileServiceClient.post('/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  },


  // Upload multiple files with shared timestamp
  uploadAllFiles: async (filesObj, timestamp = null) => {
    try {
      const results = {};
      const fileTypes = Object.keys(filesObj).filter(key => filesObj[key]);
      
      console.log(`Starting upload of ${fileTypes.length} files with timestamp: ${timestamp}`);

      // Upload files one by one
      for (const fileType of fileTypes) {
        const file = filesObj[fileType];
        if (!file) continue;

        console.log(`Uploading ${fileType}: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);

        try {
          const result = await fileService.uploadFile(file, fileType, timestamp);
          results[fileType] = result;
          console.log(`✓ Successfully uploaded ${fileType}`);
        } catch (error) {
          console.error(`✗ Failed to upload ${fileType}:`, error);
          results[fileType] = { 
            success: false, 
            error: error.message || 'Upload failed',
            file_name: file.name
          };
        }
      }

      // Check if any uploads succeeded
      const successfulUploads = Object.values(results).filter(r => r.success).length;
      const totalUploads = Object.keys(results).length;

      return {
        success: successfulUploads > 0,
        message: `${successfulUploads}/${totalUploads} files uploaded successfully`,
        results: results,
        timestamp: timestamp
      };
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    }
  },

  // List files
  listFiles: async (fileType = null, limit = 50, offset = 0) => {
    try {
      const params = { limit, offset };
      if (fileType) {
        params.file_type = fileType;
      }

      const response = await fileServiceClient.get('/files', { params });
      return response.data;
    } catch (error) {
      console.error('Error listing files:', error);
      throw error;
    }
  },


  // Download file content (optionally specify bucket and path)
  downloadFile: async (filename, bucket = null, path = 'uploads') => {
    try {
      const params = {};
      if (bucket) params.custom_bucket = bucket;
      if (path) params.s3_prefix = path;
      
      try {
        console.log(`Attempting presigned download for ${filename}`);
        const presignedResponse = await fileServiceClient.get(`/files/${filename}/presigned`, {
          params
        });
        
        if (presignedResponse.data.success && presignedResponse.data.download_url) {
          console.log(`✓ Got presigned download URL for ${filename}`);
          return { 
            success: true, 
            redirected: true, 
            url: presignedResponse.data.download_url,
            file_info: presignedResponse.data.file_info
          };
        }
      } catch (presignedError) {
        // If presigned URL fails (e.g., local storage doesn't support it), fall back to content endpoint
        console.log(`Presigned download not available for ${filename}, falling back to content endpoint:`, presignedError.message);
      }
      
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  },

  // Generate pre-signed download URL (new method)
  generatePresignedDownloadUrl: async (filename, bucket = null, path = 'uploads', expiresIn = 3600) => {
    try {
      const params = { expires_in: expiresIn };
      if (bucket) params.custom_bucket = bucket;
      if (path) params.s3_prefix = path;
      
      const response = await fileServiceClient.get(`/files/${filename}/presigned`, { params });
      return response.data;
    } catch (error) {
      console.error('Error generating presigned download URL:', error);
      throw error;
    }
  },


  // Delete file
  deleteFile: async (filename) => {
    try {
      const response = await fileServiceClient.delete(`/files/${filename}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  },

  // Search files
  searchFiles: async (searchCriteria) => {
    try {
      const response = await fileServiceClient.post('/files/search', searchCriteria);
      return response.data;
    } catch (error) {
      console.error('Error searching files:', error);
      throw error;
    }
  },

  // Generate pre-signed URL for large file uploads
  generatePresignedUrl: async (filename, fileType, contentType = 'text/csv') => {
    try {
      const response = await fileServiceClient.post('/files/presigned-url', {
        filename,
        file_type: fileType,
        content_type: contentType
      });
      return response.data;
    } catch (error) {
      console.error('Error generating pre-signed URL:', error);
      throw error;
    }
  },

  // Confirm file upload via pre-signed URL
  confirmUpload: async (s3Key, fileType, originalFilename) => {
    try {
      console.log(`Confirming upload for ${originalFilename}...`);
      
      const response = await fileServiceClient.post('/files/confirm-upload', {
        s3_key: s3Key,
        file_type: fileType,
        original_filename: originalFilename
      }, {
        timeout: 120000, // 2 minutes timeout for large file processing
      });
      
      console.log('Upload confirmation completed successfully');
      return response.data;
    } catch (error) {
      console.error('Error confirming upload:', error);
      
      // Provide more detailed error information
      if (error.response?.data?.detail) {
        console.error('Backend error details:', error.response.data.detail);
        throw new Error(`Upload confirmation failed: ${error.response.data.detail}`);
      } else if (error.code === 'ECONNABORTED') {
        throw new Error('Upload confirmation timed out - file may be too large for processing');
      } else {
        throw error;
      }
    }
  },

  // Search files with glob pattern
  searchFilesWithPattern: async (pattern, storagePath = null) => {
    try {
      const response = await fileServiceClient.post('/files/search-pattern', {
        pattern,
        storage_path: storagePath
      });
      return response.data;
    } catch (error) {
      console.error('Error searching files with pattern:', error);
      throw error;
    }
  },

  // Upload large files using presigned URLs
  uploadLargeFile: async (file, fileType, timestamp = null) => {
    try {
      console.log(`Starting large file upload for ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
      
      // Step 1: Generate presigned URL
      const filename = timestamp ? `${fileType}_${timestamp}.csv` : `${fileType}.csv`;
      const presignedResponse = await fileService.generatePresignedUrl(
        filename,
        fileType,
        file.type || 'text/csv'
      );

      if (!presignedResponse.success) {
        throw new Error('Failed to generate presigned URL');
      }

      console.log('Generated presigned URL, uploading to S3...');

      // Step 2: Upload directly to S3 using presigned URL
      const uploadResponse = await axios.put(presignedResponse.upload_url, file, {
        headers: {
          'Content-Type': file.type || 'text/csv',
        },
        // Add upload progress tracking
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
        },
      });

      if (uploadResponse.status !== 200) {
        throw new Error(`S3 upload failed with status ${uploadResponse.status}`);
      }

      console.log('File uploaded to S3, confirming upload...');

      // Step 3: Confirm upload with backend (this triggers parquet conversion)
      // Add retry logic for confirm upload
      let confirmResponse;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (retryCount < maxRetries) {
        try {
          confirmResponse = await fileService.confirmUpload(
            presignedResponse.s3_key,
            fileType,
            file.name
          );
          break; // Success, exit retry loop
        } catch (error) {
          retryCount++;
          if (retryCount >= maxRetries) {
            console.error(`Confirm upload failed after ${maxRetries} attempts`);
            throw error;
          }
          
          console.log(`Confirm upload failed (attempt ${retryCount}/${maxRetries}), retrying in 2 seconds...`);
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }

      if (!confirmResponse.success) {
        throw new Error('Failed to confirm upload');
      }

      console.log('✓ Large file upload completed successfully');
      return confirmResponse;

    } catch (error) {
      console.error('Error uploading large file:', error);
      throw error;
    }
  },

  /**
   * Upload a single file to an explicit S3 path via the file service.
   * Uses custom_path so the file lands exactly at
   *   s3://<default-bucket>/<customPath>
   *
   * @param {File}   file       - Browser File object
   * @param {string} customPath - Full S3 key (e.g. "temp-doc-chat/abc123/report.pdf")
   * @returns {Promise<FileOperationResponse>}
   */
  uploadFileToPath: async (file, customPath) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', 'doc_chat');
    formData.append('convert_to_parquet', 'false');
    formData.append('custom_path', customPath);

    try {
      const response = await fileServiceClient.post('/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading file to path:', error);
      throw error;
    }
  },
};

export default fileService;
